// File: src/pages/UnitsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap";
import { RefreshCcw, Plus, Trash2 } from "react-feather";
import swal from "sweetalert";

// ✅ real APIs you provided
import {
  _PostSave,
  _GetByUrl,
  deleteUnits,
} from "../import/ApiEndPoint/ApiCalling";
import { saveUnit, viewUnits } from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("unitmaster-inline-css")) return;
  const s = document.createElement("style");
  s.id = "unitmaster-inline-css";
  s.textContent = `
    .u-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .u-toolbar{ display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
    .u-title{ font-weight:800; text-transform:uppercase; font-size:18px; }
    .u-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .u-btn{ background:#0b5cf6; color:#fff; font-weight:700; height:34px; }
    .u-muted{ font-size:12px; color:#6b7280; }
    .u-list{ display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:8px; }
    .u-item{ display:flex; gap:8px; align-items:center; padding:8px; background:#fff; border:1px solid #e5e7eb; border-radius:10px; }
    .u-check{ width:14px; height:14px; }
    .u-subtitle{ font-weight:700; font-size:14px; margin-bottom:8px; }
  `;
  document.head.appendChild(s);
};

const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

// Your full “dummyUnits” transformed → {name, short}
const RAW_PRESETS = [
  { unitValue: "Bag", unitName: "BAGS(Bag)" },
  { unitValue: "Btl", unitName: "BOTTLES(Btl)" },
  { unitValue: "Box", unitName: "BOX(Box)" },
  { unitValue: "Bdl", unitName: "BUNDLES(Bdl)" },
  { unitValue: "Can", unitName: "CANS(Can)" },
  { unitValue: "Ctn", unitName: "CARTONS(Ctn)" },
  { unitValue: "Dzn", unitName: "DOZENS(Dzn)" },
  { unitValue: "Gm", unitName: "GRAMMES(Gm)" },
  { unitValue: "Kg", unitName: "KILOGRAMS(Kg)" },
  { unitValue: "Ltr", unitName: "LITRE(Ltr)" },
  { unitValue: "Mtr", unitName: "METERS(Mtr)" },
  { unitValue: "Ml", unitName: "MILILITRE(Ml)" },
  { unitValue: "Nos", unitName: "NUMBERS(Nos)" },
  { unitValue: "Pac", unitName: "PACKS(Pac)" },
  { unitValue: "Prs", unitName: "PAIRS(Prs)" },
  { unitValue: "Pcs", unitName: "PIECES(Pcs)" },
  { unitValue: "Qtl", unitName: "QUINTAL(Qtl)" },
  { unitValue: "Rol", unitName: "ROLLS(Rol)" },
  { unitValue: "Sqf", unitName: "SQUARE FEET(Sqf)" },
  { unitValue: "kg", unitName: "Kilogram(kg)" },
  { unitValue: "Pcs", unitName: "Pieces(Pcs)" },
  { unitValue: "g", unitName: "Gram(g)" },
  { unitValue: "tonne", unitName: "Metric Ton(tonne)" },
  { unitValue: "m", unitName: "Meter(m)" },
  { unitValue: "cm", unitName: "Centimeter(cm)" },
  { unitValue: "mm", unitName: "Millimeter(mm)" },
  { unitValue: "in", unitName: "Inch(in)" },
  { unitValue: "ft", unitName: "Foot(ft)" },
  { unitValue: "m3", unitName: "Cubic Meter(m³)" },
  { unitValue: "L", unitName: "Liter(L)" },
  { unitValue: "ml", unitName: "Milliliter(ml)" },
  { unitValue: "s", unitName: "Second(s)" },
  { unitValue: "min", unitName: "Minute(min)" },
  { unitValue: "hr", unitName: "Hour(hr)" },
  { unitValue: "°C", unitName: "Celsius(°C)" },
  { unitValue: "°F", unitName: "Fahrenheit(°F)" },
  { unitValue: "Pa", unitName: "Pascal(Pa)" },
  { unitValue: "bar", unitName: "Bar(bar)" },
  { unitValue: "m/s", unitName: "Meters per Second(m/s)" },
  { unitValue: "km/h", unitName: "Kilometers per Hour(km/h)" },
  { unitValue: "A", unitName: "Ampere(A)" },
  { unitValue: "V", unitName: "Volt(V)" },
  { unitValue: "W", unitName: "Watt(W)" },
  { unitValue: "kW", unitName: "Kilowatt(kW)" },
];

const dedupPresets = () => {
  const map = new Map();
  RAW_PRESETS.forEach(({ unitValue, unitName }) => {
    const m = /^(.*)\((.*)\)$/.exec(unitName.trim());
    const name = (m?.[1] || unitName).trim();
    const short = unitValue.trim();
    const key = `${name.toUpperCase()}|${short.toLowerCase()}`;
    if (!map.has(key)) map.set(key, { name, short });
  });
  return Array.from(map.values());
};

const PRESETS = dedupPresets();
const labelOf = (u) => `${u.name}(${u.short})`;

export default function UnitsPage() {
  useEffect(() => injectOnce(), []);

  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [unitContainerId, setUnitContainerId] = useState(""); // dynamicUnit._id
  const [units, setUnits] = useState([]); // [{_id, unitId, unitName}]

  // A) pick presets
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  // B) create manually
  const [form, setForm] = useState({ name: "", short: "" });
  const [savingPreset, setSavingPreset] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      if (!user?.database) {
        setUnits([]);
        setUnitContainerId("");
        return;
      }
      const res = await _GetByUrl(viewUnits, user.database);
      const arr = Array.isArray(res?.dynamicUnit?.Units)
        ? res.dynamicUnit.Units
        : [];
      setUnits(arr);
      setUnitContainerId(res?.dynamicUnit?._id || "");
    } catch (err) {
      setUnits([]);
      setUnitContainerId("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  // for disabling presets already added
  const existingKeys = useMemo(() => {
    const s = new Set();
    units.forEach((u) => {
      const name = (u?.unitName || "").toUpperCase();
      const short = (u?.unitId || "").toLowerCase();
      s.add(`${name}|${short}`);
    });
    return s;
  }, [units]);

  const filteredPresets = useMemo(() => {
    const t = search.trim().toLowerCase();
    return PRESETS.filter((u) => {
      if (t) {
        const str = `${u.name} ${u.short}`.toLowerCase();
        if (!str.includes(t)) return false;
      }
      return true;
    });
  }, [search]);

  const togglePreset = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const addSelectedPresets = async (e) => {
    e.preventDefault();
    if (selected.size === 0) {
      return swal("Pick at least one unit", "", "warning");
    }
    const payloadUnits = Array.from(selected).map((lab) => {
      const m = /^(.*)\((.*)\)$/.exec(lab);
      const name = (m?.[1] || lab).trim();
      const short = (m?.[2] || "").trim();
      return { unitName: name, unitId: short };
    });

    setSavingPreset(true);
    try {
      const obj = {
        created_by: user?._id,
        database: user?.database,
        Units: payloadUnits,
      };
      const res = await _PostSave(saveUnit, obj);
      if (res?.status) {
        swal("Success", "Selected units added", "success");
        setSelected(new Set());
        fetchUnits();
      } else {
        swal("Error", res?.message || "Could not add units", "error");
      }
    } catch {
      swal("Error", "Could not add units", "error");
    } finally {
      setSavingPreset(false);
    }
  };

  const addCustom = async (e) => {
    e.preventDefault();
    const name = (form.name || "").trim();
    const short = (form.short || "").trim();
    if (!name || !short) {
      return swal("Error", "Enter both Name and Short Id", "error");
    }
    setSavingCustom(true);
    try {
      const obj = {
        created_by: user?._id,
        database: user?.database,
        Units: [{ unitName: name, unitId: short }],
      };
      const res = await _PostSave(saveUnit, obj);
      if (res?.status) {
        swal("Success", "Unit created successfully", "success");
        setForm({ name: "", short: "" });
        fetchUnits();
      } else {
        swal("Error", res?.message || "Could not add unit", "error");
      }
    } catch {
      swal("Error", "Could not add unit", "error");
    } finally {
      setSavingCustom(false);
    }
  };

  const onDelete = async (unitItemId) => {
    if (!unitContainerId || !unitItemId) return;
    const ok = await swal({
      title: "Delete this unit?",
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!ok) return;
    try {
      const res = await deleteUnits(unitContainerId, unitItemId);
      if (res?.status) {
        swal("Deleted", "Unit deleted successfully", "success");
        fetchUnits();
      } else {
        swal("Error", res?.message || "Delete failed", "error");
      }
    } catch {
      swal("Error", "Delete failed", "error");
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="u-card"
            style={{ padding: 10, background: "#f4f7f6" }}
          >
            <div className="u-toolbar">
              <div className="u-title">
                Unit Master <span className="u-pill">{units.length}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Button
                outline
                color="secondary"
                style={{ height: 34 }}
                onClick={fetchUnits}
                title="Refresh"
              >
                <RefreshCcw size={16} />
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* A) Predefined */}
        <Col lg="7" md="12">
          <Card className="u-card">
            <CardBody>
              <div className="u-toolbar" style={{ marginBottom: 8 }}>
                <div
                  className="u-title"
                  style={{ fontSize: 16, textTransform: "none" }}
                >
                  A) Pick from Predefined Units
                </div>
                <div style={{ flex: "1 1 240px" }} />
                <Input
                  style={{ maxWidth: 280, background: "#fff" }}
                  placeholder="Search unit or short id…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="u-list">
                {filteredPresets.map((u) => {
                  const key = labelOf(u); // "NAME(short)"
                  const already = existingKeys.has(
                    `${u.name.toUpperCase()}|${u.short.toLowerCase()}`
                  );
                  const checked = selected.has(key);
                  return (
                    <label
                      key={key}
                      className="u-item"
                      title={key}
                      style={{ opacity: already ? 0.6 : 1 }}
                    >
                      <input
                        type="checkbox"
                        className="u-check"
                        disabled={already}
                        checked={checked}
                        onChange={() => togglePreset(key)}
                      />
                      <div>
                        <div style={{ fontWeight: 700 }}>{u.name}</div>
                        <div className="u-muted">{u.short}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <Button
                  className="u-btn"
                  onClick={addSelectedPresets}
                  disabled={savingPreset || selected.size === 0}
                >
                  {savingPreset ? "Saving…" : <>Add Selected</>}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* B) Create + Saved Unit List below */}
        <Col lg="5" md="12">
          {/* B) Create New Unit */}
          <Card className="u-card">
            <CardBody>
              <div
                className="u-title"
                style={{ fontSize: 16, textTransform: "none", marginBottom: 8 }}
              >
                B) Create New Unit
              </div>
              <Form onSubmit={addCustom}>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., KILOGRAMS"
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Short Id</Label>
                  <Input
                    placeholder="e.g., Kg"
                    value={form.short}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, short: e.target.value }))
                    }
                  />
                </FormGroup>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    className="u-btn"
                    type="submit"
                    disabled={savingCustom}
                  >
                    {savingCustom ? (
                      "Saving…"
                    ) : (
                      <>
                        {" "}
                        <Plus size={14} />
                        &nbsp;Add Unit
                      </>
                    )}
                  </Button>
                  <div className="u-muted" style={{ alignSelf: "center" }}>
                    Will save as <code>unitName = Name</code>,{" "}
                    <code>unitId = Short</code>
                  </div>
                </div>
              </Form>
            </CardBody>
          </Card>

          {/* Saved Unit List (new card below B) */}
          <Card className="u-card" style={{ marginTop: 10 }}>
            <CardBody>
              <div
                className="u-title"
                style={{ fontSize: 16, textTransform: "none", marginBottom: 8 }}
              >
                Saved Unit List
              </div>
              {loading ? (
                <div className="u-muted">Loading…</div>
              ) : units.length === 0 ? (
                <div className="u-muted">No units yet.</div>
              ) : (
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th>Name</th>
                      <th>Short Id</th>
                      <th style={{ width: 80 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.map((u, i) => (
                      <tr key={u?._id || i}>
                        <td>{i + 1}</td>
                        <td>{u?.unitName}</td>
                        <td>{u?.unitId}</td>
                        <td>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => onDelete(u?._id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
